import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  portalApi,
  type DsarRequestType,
  type PortalCase,
  type PortalDsarRequest,
} from '../lib/api.ts';
import { formatDateTime, humanize, statusTone } from '../lib/format.ts';
import { describeError, type ErrorInfo } from '../lib/request-id.ts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Notice,
  Select,
  SkeletonText,
  Table,
  Td,
  Textarea,
  Th,
} from './ui.tsx';

interface Props {
  cases: PortalCase[];
}

const REQUEST_TYPES: DsarRequestType[] = ['access', 'rectification', 'erasure'];

const TYPE_HELP: Record<DsarRequestType, string> = {
  access: 'A copy of the personal data held about you in this case.',
  rectification: 'Correct personal data that is inaccurate or incomplete.',
  erasure: 'Delete personal data your firm no longer needs to keep.',
};

/**
 * The client self-service DSAR view (T-7.14; US-153; NDPA Part V). The ONE mutating
 * affordance in the otherwise read-only portal beyond the T-7.11 upload: a client SUBMITS a
 * data-subject request (access | rectification | erasure) for its OWN data in a case it is a
 * member of (CC-1). The client can ONLY request — a firm admin fulfils it (CC-2). The view
 * also lists the client's own submitted requests with their fulfilment status. Errors surface
 * as the typed, non-revealing message (a 404 not-your-case reads as a friendly line — CC-7).
 */
export function DsarRequestForm({ cases }: Props): JSX.Element {
  const [caseNumber, setCaseNumber] = useState<string>(cases[0]?.case_number ?? '');
  const [requestType, setRequestType] = useState<DsarRequestType>('access');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);

  const [requests, setRequests] = useState<PortalDsarRequest[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [listError, setListError] = useState<ErrorInfo | null>(null);

  const refresh = useCallback(() => {
    void (async () => {
      try {
        setRequests(await portalApi.listDsarRequests());
        setListError(null);
      } catch (err) {
        setListError(describeError(err));
      } finally {
        setListLoaded(true);
      }
    })();
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!caseNumber || busy) return;
    setBusy(true);
    setError(null);
    setSubmitted(null);
    void (async () => {
      try {
        const req = await portalApi.submitDsarRequest(
          caseNumber,
          requestType,
          details.trim() ? details.trim() : undefined,
        );
        setSubmitted(req.id);
        setDetails('');
        refresh();
      } catch (err) {
        setError(describeError(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section aria-label="Data-subject requests">
      <div className="portal-grid portal-grid--even">
        <div className="portal-grid__column">
          <Card>
            <CardHeader
              title="Submit a request"
              description="Request access to, correction of, or erasure of your personal data held in one of your cases (NDPA Part V): choose the case the request concerns and the right you want to exercise. Your firm reviews and fulfils each request."
            />
            <CardBody>
              <form
                className="portal-form"
                aria-label="Submit a data-subject request"
                onSubmit={onSubmit}
              >
                <Select
                  id="dsar-case"
                  label="Case"
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                  disabled={busy || cases.length === 0}
                  help={
                    cases.length === 0
                      ? 'You have no cases yet, so there is nothing to request against.'
                      : undefined
                  }
                >
                  {cases.map((c) => (
                    <option key={c.case_number} value={c.case_number}>
                      {c.case_number} — {c.title}
                    </option>
                  ))}
                </Select>

                <Select
                  id="dsar-type"
                  label="Request type"
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value as DsarRequestType)}
                  disabled={busy}
                  help={TYPE_HELP[requestType]}
                >
                  {REQUEST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </Select>

                <Textarea
                  id="dsar-details"
                  label="Details"
                  optional
                  value={details}
                  maxLength={2000}
                  onChange={(e) => setDetails(e.target.value)}
                  disabled={busy}
                  placeholder="Which field to correct, or the reason for the request"
                  help="Up to 2,000 characters."
                />

                <div className="portal-form__actions">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={busy}
                    disabled={!caseNumber || busy}
                  >
                    {busy ? 'Submitting…' : 'Submit request'}
                  </Button>
                </div>

                {error ? (
                  <ErrorState
                    title="Request not submitted"
                    message={error.message}
                    requestId={error.requestId}
                  />
                ) : null}
                {submitted ? (
                  <Notice tone="success">
                    Your request was submitted. Your firm will review it.
                  </Notice>
                ) : null}
              </form>
            </CardBody>
          </Card>
        </div>

        <div className="portal-grid__column">
          <Card>
            <CardHeader
              title="Your requests"
              description="Each request and where your firm has got to with it."
            />
            <CardBody flush={listLoaded && !listError && requests.length > 0}>
              {!listLoaded ? (
                <SkeletonText lines={3} label="Loading your requests" />
              ) : listError ? (
                <ErrorState
                  title="Your requests could not be loaded"
                  message={listError.message}
                  requestId={listError.requestId}
                  onRetry={refresh}
                />
              ) : requests.length === 0 ? (
                <EmptyState
                  compact
                  title="No requests yet"
                  description="You have not submitted any requests yet. Submitted requests and their outcomes appear here."
                />
              ) : (
                <Table caption="Your data-subject requests" aria-label="Your data-subject requests">
                  <thead>
                    <tr>
                      <Th>Request</Th>
                      <Th>Case</Th>
                      <Th>Status</Th>
                      <Th shrink>Requested</Th>
                      <Th>Outcome</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id}>
                        <Td>{humanize(r.request_type)}</Td>
                        <Td mono>{r.case_number}</Td>
                        <Td>
                          <Badge tone={statusTone(r.status)}>{humanize(r.status)}</Badge>
                        </Td>
                        <Td shrink className="text-muted">
                          {formatDateTime(r.requested_at)}
                        </Td>
                        <Td>
                          {r.refusal_reason ? (
                            <span className="text-muted">{humanize(r.refusal_reason)}</span>
                          ) : r.resolved_at ? (
                            <span className="text-muted">{formatDateTime(r.resolved_at)}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </section>
  );
}
